import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", paymentStatus: null, orderId: null });
  const polledRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", paymentStatus: null, orderId: null });
      return;
    }
    let cancelled = false;
    async function poll() {
      try {
        const { data } = await api.get(`/checkout/stripe/status/${sessionId}`);
        if (cancelled) return;
        setState({ status: data.status, paymentStatus: data.payment_status, orderId: data.order_id });
        if (data.payment_status === "paid" || data.status === "expired") return;
        polledRef.current += 1;
        if (polledRef.current < 8) {
          setTimeout(poll, 1500);
        }
      } catch (err) {
        if (!cancelled) setState({ status: "error", paymentStatus: null, orderId: null });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.paymentStatus === "paid";
  const expired = state.status === "expired";
  const failed = state.status === "error";

  return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="order-success-page">
      {!paid && !expired && !failed && (
        <>
          <Loader2 className="w-12 h-12 text-[var(--accent-strong)] animate-spin mx-auto" />
          <h1 className="mt-6 font-serif text-3xl">Confirming your payment…</h1>
          <p className="mt-3 text-[#5C5547]">This usually takes a few seconds. Please don't close this page.</p>
        </>
      )}
      {paid && (
        <>
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" data-testid="paid-success-icon" />
          <h1 className="mt-6 font-serif text-4xl gold-heading">Thank you!</h1>
          <p className="mt-4 text-[#5C5547]">Your payment was successful. We've emailed you a download link and an order receipt.</p>
          {state.orderId && (
            <div className="mt-6 inline-block px-5 py-3 rounded-full bg-[#F5F2EA] border border-[#EAE4D3] text-sm">
              Order ID: <span className="font-mono">{state.orderId}</span>
            </div>
          )}
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/shop" data-testid="shop-more-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold"
              style={{ background: "var(--accent-strong)", color: "#000" }}>
              Shop more
            </Link>
            <Link to="/" data-testid="home-btn"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
              Home
            </Link>
          </div>
        </>
      )}
      {(expired || failed) && (
        <>
          <AlertTriangle className="w-16 h-16 text-[var(--accent-strong)] mx-auto" />
          <h1 className="mt-6 font-serif text-3xl">Payment {expired ? "expired" : "could not be confirmed"}</h1>
          <p className="mt-3 text-[#5C5547]">No worries — you can try again or pick a different method.</p>
          <Link to="/shop" className="mt-8 inline-block px-7 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">
            Back to shop
          </Link>
        </>
      )}
    </div>
  );
}
